/**
 * ImpactDidTab — renders impact_serving.did as a filterable table.
 *
 * All numbers come from the DB; nothing is computed in the front (Manuel's rule).
 * Controls: Channel (SEO|LLM), Estimator (Exact matched|Same store), Store.
 * Cells and Coverage columns shown only for Exact matched (estimator='matched').
 */

import React, { useMemo, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DidRow {
  treatment_group: string;
  channel: 'seo' | 'llm';
  estimator: 'matched' | 'store';
  scope: string;
  period_before: string;
  period_after: string;
  treated_n: number;
  treated_before: number | null;
  treated_after: number | null;
  treated_growth: number | null;
  control_before: number | null;
  control_after: number | null;
  control_growth: number | null;
  did: number | null;
  cells: number | null;
  coverage: number | null;
}

export interface DidMeta {
  data_until: string;
  cohort_snapshot: string;
}

export interface DidData {
  meta: DidMeta;
  stores: string[];
  rows: DidRow[];
}

// ── Formatters ────────────────────────────────────────────────────────────────

const FMT_INT = new Intl.NumberFormat('en-US');
const FMT_1D  = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const FMT_3D  = new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmt_int = (n: number) => FMT_INT.format(n);
const fmt_1d  = (n: number) => FMT_1D.format(n);
const fmt_3d  = (n: number) => FMT_3D.format(n);
const fmt_pct = (n: number) => FMT_1D.format(n) + ' %';
const fmt_growth = (n: number) => (n >= 0 ? '+' : '') + FMT_1D.format(n) + ' %';
const fmt_did    = (n: number) => (n >= 0 ? '+' : '') + FMT_1D.format(n) + ' pts';

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  data: DidData;
}

export function ImpactDidTab({ data }: Props) {
  const { meta, stores, rows } = data;

  const [channel, setChannel] = useState<'seo' | 'llm'>('seo');
  const [estimator, setEstimator] = useState<'matched' | 'store'>('matched');
  const [scope, setScope] = useState<string>('global');

  const isMatched = estimator === 'matched';

  // Filtered rows for this view
  const filtered = useMemo(
    () => rows.filter(r => r.channel === channel && r.estimator === estimator && r.scope === scope),
    [rows, channel, estimator, scope]
  );

  // Waves missing for this store (present globally but absent here)
  const notMeasured = useMemo(() => {
    if (scope === 'global') return [];
    const globalWaves = new Set(
      rows
        .filter(r => r.channel === channel && r.estimator === estimator && r.scope === 'global')
        .map(r => r.treatment_group)
    );
    const storeWaves = new Set(filtered.map(r => r.treatment_group));
    return [...globalWaves].filter(w => !storeWaves.has(w)).sort();
  }, [rows, channel, estimator, scope, filtered]);

  // Derived: format before/after per estimator
  const fmtBeforeAfter = (n: number | null) =>
    n === null ? '—' : isMatched ? fmt_3d(n) : fmt_1d(n);

  return (
    <div className="space-y-4">
      {/* Header from meta */}
      <p className="text-sm text-muted-foreground">
        Data until {meta.data_until} · AEO cohort of {meta.cohort_snapshot}
      </p>

      {/* Controls */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Channel</span>
          <Select value={channel} onValueChange={v => setChannel(v as 'seo' | 'llm')}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="seo">SEO</SelectItem>
              <SelectItem value="llm">LLM</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Estimator</span>
          <Select value={estimator} onValueChange={v => setEstimator(v as 'matched' | 'store')}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="matched">Exact matched</SelectItem>
              <SelectItem value="store">Same store</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Store</span>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="global">All stores</SelectItem>
              {stores.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Wave</TableHead>
              <TableHead>Before → after</TableHead>
              <TableHead className="text-right">AEO products</TableHead>
              <TableHead className="text-right">AEO before</TableHead>
              <TableHead className="text-right">AEO after</TableHead>
              <TableHead className="text-right">AEO growth</TableHead>
              <TableHead className="text-right">Control before</TableHead>
              <TableHead className="text-right">Control after</TableHead>
              <TableHead className="text-right">Control growth</TableHead>
              <TableHead className="text-right">DiD</TableHead>
              {isMatched && <TableHead className="text-right">Cells</TableHead>}
              {isMatched && <TableHead className="text-right">Coverage</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isMatched ? 12 : 10} className="text-center text-muted-foreground py-8">
                  No data for this selection.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map(row => {
                const fewProducts = row.treated_n < 100;
                const nullDid = row.did === null;

                return (
                  <TableRow
                    key={`${row.channel}-${row.estimator}-${row.scope}-${row.treatment_group}`}
                    className={fewProducts ? 'opacity-50' : undefined}
                  >
                    <TableCell className="font-medium whitespace-nowrap">
                      {row.treatment_group}
                      {fewProducts && (
                        <span
                          className="ml-1 cursor-help"
                          title="Based on few products: indicative only"
                        >
                          ⚠
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {row.period_before} → {row.period_after}
                    </TableCell>
                    <TableCell className="text-right">
                      {fmt_int(row.treated_n)}
                    </TableCell>

                    {nullDid ? (
                      <>
                        <TableCell className="text-right text-muted-foreground">—</TableCell>
                        <TableCell className="text-right text-muted-foreground">—</TableCell>
                        <TableCell className="text-right text-muted-foreground">—</TableCell>
                        <TableCell className="text-right text-muted-foreground">—</TableCell>
                        <TableCell className="text-right text-muted-foreground">—</TableCell>
                        <TableCell className="text-right text-muted-foreground">—</TableCell>
                        <TableCell
                          className="text-right"
                          title="Not enough comparable products"
                        >
                          <div className="text-muted-foreground">—</div>
                          <div className="text-xs text-muted-foreground">no match</div>
                        </TableCell>
                        {isMatched && <TableCell className="text-right">{row.cells ?? '—'}</TableCell>}
                        {isMatched && <TableCell className="text-right">{row.coverage !== null ? fmt_pct(row.coverage) : '—'}</TableCell>}
                      </>
                    ) : (
                      <>
                        <TableCell className="text-right">{fmtBeforeAfter(row.treated_before)}</TableCell>
                        <TableCell className="text-right">{fmtBeforeAfter(row.treated_after)}</TableCell>
                        <TableCell className="text-right">
                          {row.treated_growth !== null ? fmt_growth(row.treated_growth) : '—'}
                        </TableCell>
                        <TableCell className="text-right">{fmtBeforeAfter(row.control_before)}</TableCell>
                        <TableCell className="text-right">{fmtBeforeAfter(row.control_after)}</TableCell>
                        <TableCell className="text-right">
                          {row.control_growth !== null ? fmt_growth(row.control_growth) : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <span
                            className={`font-bold ${row.did > 0 ? 'text-green-600' : row.did < 0 ? 'text-red-600' : 'text-muted-foreground'}`}
                          >
                            {fmt_did(row.did)}
                          </span>
                        </TableCell>
                        {isMatched && <TableCell className="text-right">{row.cells !== null ? fmt_int(row.cells) : '—'}</TableCell>}
                        {isMatched && <TableCell className="text-right">{row.coverage !== null ? fmt_pct(row.coverage) : '—'}</TableCell>}
                      </>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* "Not measured" footer */}
      {notMeasured.length > 0 && (
        <p className="text-sm text-muted-foreground">
          Not measured in this store (fewer than 100 products): {notMeasured.join(', ')}
        </p>
      )}

      {/* Reader notes */}
      <div className="text-xs text-muted-foreground space-y-1 border-t pt-3">
        {isMatched && (
          <p>
            <strong>Matched:</strong> the growths are not after ÷ before of the columns shown.
            Each cell has its own growth and they are averaged; before/after are shown for scale.
            Same for the DiD: it is computed by the pipeline, not in the front.
          </p>
        )}
        <p>
          <strong>AEO products</strong> differs between estimators: in Same store it is every product
          of the wave; in Exact matched only those that found a cell with enough products.
        </p>
        {isMatched && (
          <p>
            <strong>Cells:</strong> store × decile × band combinations with ≥5 AEO and control
            products with visits before. <strong>Coverage:</strong> share of AEO products with
            visits before that fell in a valid cell.
          </p>
        )}
      </div>
    </div>
  );
}
